import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User, Session } from '@/types';
import { authApi } from '@/api/client';

const SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24小时
const TOKEN_REFRESH_THRESHOLD_MS = 30 * 60 * 1000; // 30分钟前刷新
const SESSION_EXPIRY_WARNING_MS = 60 * 60 * 1000; // 剩余1小时时提醒

function isSessionValid(session: Session | null): boolean {
  if (!session || !session.access_token) return false;
  const loginTime = session.login_time || 0;
  if (loginTime === 0) return false;
  return Date.now() - loginTime < SESSION_DURATION_MS;
}

function getSessionRemainingTime(session: Session | null): number {
  if (!session || !session.login_time) return 0;
  const elapsed = Date.now() - session.login_time;
  return Math.max(0, SESSION_DURATION_MS - elapsed);
}

function shouldRefreshToken(session: Session | null): boolean {
  if (!session || !session.expires_at) return true;
  // Refresh if token expires within 10 minutes
  const expiresAt = session.expires_at * 1000; // Convert to milliseconds
  return Date.now() > (expiresAt - TOKEN_REFRESH_THRESHOLD_MS);
}

interface AuthState {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  sessionRemainingTime: number;
  
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  verifySession: () => Promise<void>;
  refreshSession: () => Promise<boolean>;
  setUser: (user: User | null) => void;
  setSession: (session: Session | null) => void;
  updateSessionRemainingTime: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      session: null,
      isLoading: false,
      isAuthenticated: false,
      sessionRemainingTime: 0,

      updateSessionRemainingTime: () => {
        const { session } = get();
        const remaining = getSessionRemainingTime(session);
        set({ sessionRemainingTime: remaining });
      },

      login: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const { user, session } = await authApi.login(email, password);
          const sessionWithLoginTime: Session = {
            ...session,
            login_time: Date.now(),
          };
          set({ user, session: sessionWithLoginTime, isAuthenticated: true });
          localStorage.setItem('session', JSON.stringify(sessionWithLoginTime));
        } catch (error) {
          console.error('Login error:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      register: async (email: string, password: string) => {
        set({ isLoading: true });
        try {
          const { user, session } = await authApi.register(email, password);
          const sessionWithLoginTime: Session = {
            ...session,
            login_time: Date.now(),
          };
          set({ user, session: sessionWithLoginTime, isAuthenticated: true });
          localStorage.setItem('session', JSON.stringify(sessionWithLoginTime));
        } catch (error) {
          console.error('Register error:', error);
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      logout: async () => {
        try {
          await authApi.logout();
        } catch (error) {
          console.error('Logout error:', error);
        } finally {
          set({ user: null, session: null, isAuthenticated: false });
          localStorage.removeItem('session');
        }
      },

      refreshSession: async () => {
        const { session } = get();
        if (!session?.refresh_token) {
          console.log('No refresh token available');
          return false;
        }

        try {
          console.log('Refreshing session...');
          const { session: newSession } = await authApi.refreshSession(session.refresh_token);
          if (newSession) {
            console.log('New access token received:', newSession.access_token?.substring(0, 20) + '...');
            const sessionWithTime: Session = {
              ...newSession,
              login_time: get().session?.login_time || Date.now(),
            };
            set({ session: sessionWithTime });
            localStorage.setItem('session', JSON.stringify(sessionWithTime));
            console.log('Session refreshed successfully');
            return true;
          }
        } catch (error) {
          console.error('Failed to refresh session:', error);
        }
        return false;
      },

      verifySession: async () => {
        const storedSession = localStorage.getItem('session');
        let session: Session | null = null;
        
        if (storedSession) {
          try {
            session = JSON.parse(storedSession);
          } catch {
            session = null;
          }
        }

        // Check if session is within 24-hour window
        if (!isSessionValid(session)) {
          console.log('Session expired (24-hour window)');
          set({ user: null, session: null, isAuthenticated: false });
          localStorage.removeItem('session');
          return;
        }

        // Check if token needs refresh
        if (shouldRefreshToken(session)) {
          console.log('Token needs refresh');
          const { refreshSession } = get();
          const refreshed = await refreshSession();
          if (!refreshed) {
            console.log('Failed to refresh token, logging out');
            set({ user: null, session: null, isAuthenticated: false });
            localStorage.removeItem('session');
            return;
          }
          // Update session reference after refresh
          session = get().session;
        }

        // Try to get stored user first
        let user: User | null = null;
        const authStorage = localStorage.getItem('auth-storage');
        if (authStorage) {
          try {
            const parsedStorage = JSON.parse(authStorage);
            user = parsedStorage?.state?.user || null;
          } catch {
            user = null;
          }
        }

        set({ 
          user, 
          session, 
          isAuthenticated: true 
        });

        // Verify with backend
        set({ isLoading: true });
        try {
          // Use the current session's access token directly
          const currentSession = get().session;
          const accessToken = currentSession?.access_token;
          console.log('Verifying with token:', accessToken?.substring(0, 20) + '...');
          const verifiedUser = await authApi.verify(accessToken);
          set({ user: verifiedUser, isAuthenticated: true });
        } catch (error) {
          console.error('Session verification error:', error);
          // If verification fails, try to refresh token once more
          const { refreshSession } = get();
          const refreshed = await refreshSession();
          if (refreshed) {
            try {
              // Use the new token after refresh
              const newSession = get().session;
              const newAccessToken = newSession?.access_token;
              console.log('Retry verify with new token:', newAccessToken?.substring(0, 20) + '...');
              const verifiedUser = await authApi.verify(newAccessToken);
              set({ user: verifiedUser, isAuthenticated: true });
              return;
            } catch (retryError) {
              console.error('Retry verification failed:', retryError);
            }
          }
          // If still failing, logout
          set({ user: null, session: null, isAuthenticated: false });
          localStorage.removeItem('session');
        } finally {
          set({ isLoading: false });
        }
      },

      setUser: (user) => set({ user }),
      setSession: (session) => set({ session }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, session: state.session }),
    }
  )
);
